import axios from "axios";
import useCourseSearch, { useSearchDraft } from "../hooks/useCourseSearch";
import CourseSearchFilters from "../components/CourseSearchFilters";
import useCategories from "../hooks/useCategories";
import useCourseFilterOptions from "../hooks/useCourseFilterOptions";
import { PROXY } from "../lib/utils";
import { useTranslation } from "react-i18next";
import CourseItem from "../components/CourseItem";
import { useEffect, useState } from "react";
import {
  Spinner,
  Button,
  Typography,
} from "@material-tailwind/react";
import {
  FaSearch,
  FaFilter,
  FaTimes,
} from "react-icons/fa";

const COURSES_PER_PAGE = 9;

function CourseSearchBox({ value, update, loading, t }) {
  const [draft, setDraft] = useSearchDraft(value, (text) => update("keyword", text, true));
  return <div className="relative mx-auto w-full max-w-3xl">
    <FaSearch aria-hidden="true" className="absolute start-5 top-1/2 -translate-y-1/2 text-indigo-500" />
    <input type="search" value={draft} onChange={(event) => setDraft(event.target.value)} maxLength={100} placeholder={t("courseSearch.keyword")} aria-label={t("courseSearch.keyword")} className="course-search-input w-full rounded-2xl border border-indigo-100 bg-white py-4 ps-12 pe-20 text-base shadow-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 sm:py-5 sm:text-lg" />
    {draft && <button type="button" onClick={() => setDraft("")} className="absolute end-4 top-1/2 -translate-y-1/2 rounded-full p-2 text-gray-500 hover:bg-gray-100 focus:ring-2 focus:ring-indigo-500" aria-label={t("courseSearch.clearSearch", "Clear search")}><span aria-hidden="true">&times;</span></button>}
    {loading && <span className="absolute end-12 top-1/2 -translate-y-1/2" aria-label={t("courseSearch.loading", "Loading")}><Spinner color="indigo" className="h-4 w-4" /></span>}
  </div>;
}

const Courses = () => {
  const { t, i18n } = useTranslation();

  // UI States
  const [openMobileFilters, setOpenMobileFilters] = useState(false);

  const { params, query, update, updateFilters, reset } = useCourseSearch();
  const { data: categories } = useCategories();
  const { teachers, universities } = useCourseFilterOptions();
  const [courses, setCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const resetFilters = reset;

  useEffect(() => {
    const controller = new AbortController();
    const request = new URLSearchParams(query);
    request.set("limit", COURSES_PER_PAGE);
    // Keep the existing ranking links working.
    if (["views", "averageRating"].includes(request.get("q")) && !request.has("keyword")) {
      request.set("sortField", request.get("q"));
      request.delete("q");
    }
    setLoading(true);
    setError(null);
    axios.get(`${PROXY}/courses/categories/client?${request}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setCourses(data.courses);
        setTotal(data.totalCourses);
        setTotalPages(data.totalPages);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [query, retry]);

  useEffect(() => {
    if (!openMobileFilters) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenMobileFilters(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openMobileFilters]);

  const filterFields = <CourseSearchFilters params={params} updateFilters={updateFilters} reset={reset} showSorting={false} />;
  const activeFilters = [...params.entries()].filter(([key]) => !["page", "limit", "sortField", "sortOrder"].includes(key));
  const sortOptions = [
    { key: "mostLiked", sortField: "likesCount", sortOrder: "-1" },
    { key: "latest", sortField: "createdAt", sortOrder: "-1" },
    { key: "oldest", sortField: "createdAt", sortOrder: "1" },
  ];
  const currentSortField = params.get("sortField") || "createdAt";
  const currentSortOrder = params.get("sortOrder") || "-1";
  const isEnglish = i18n.language?.startsWith("en");
  const filterValue = (key, value) => {
    if (key === "keyword" || key === "q") return value;
    if (["category", "subCategoryId", "university", "instructor"].includes(key)) {
      return value.split(",").map((id) => {
        const category = categories.find((item) => item._id === id);
        const department = categories.flatMap((item) => item.subCategories || []).find((item) => item._id === id);
        const university = universities.find((item) => item._id === id);
        const teacher = teachers.find((item) => item._id === id);
        return category?.name?.[i18n.language] || category?.name?.en || department?.[i18n.language] || department?.en || university?.name?.[i18n.language] || university?.name?.en || teacher?.fullName || id;
      }).join(", ");
    }
    return t(`courseSearch.${value}`, value);
  };

  return (
    <section className="mx-auto my-6 max-w-[1440px] px-4 sm:px-6" id="courses">
      <div className="mb-6">
        <CourseSearchBox value={params.get("keyword") || ""} update={update} loading={loading} t={t} />
        <div className="mt-3 flex justify-end lg:hidden">
        <Button
          variant="outlined"
          color="indigo"
          size="sm"
          className="flex items-center gap-2 border-indigo-100 text-indigo-600"
          aria-expanded={openMobileFilters}
          aria-controls="mobile-course-filters"
          onClick={() => setOpenMobileFilters(!openMobileFilters)}
        >
          <FaFilter size={12} /> {t("filters", "Filters")}
        </Button>
        </div>
      </div>

      <div className={`grid items-start gap-6 ${isEnglish ? "lg:grid-cols-[19rem_minmax(0,1fr)]" : "lg:grid-cols-[minmax(0,1fr)_19rem]"}`}>
        <main className={`min-w-0 flex-1 ${isEnglish ? "lg:col-start-2 lg:row-start-1" : ""}`} aria-busy={loading}>
          <div className="mb-6 grid grid-cols-[1fr_auto] items-center border-b border-slate-200 pb-3">
            <p className="text-sm font-medium text-slate-600" aria-live="polite">
              {loading ? t("searching", "Searching...") : t("courses_found", "{{count}} Courses available", { count: total })}
            </p>
            <div className="flex items-center gap-1" role="tablist" aria-label={t("courseSearch.sortBy")}>
              <span className="hidden text-sm font-medium text-slate-500 xl:inline">{t("courseSearch.sortBy")}</span>
              {sortOptions.map((option) => {
                const selected = currentSortField === option.sortField && currentSortOrder === option.sortOrder;
                return <button key={option.key} type="button" role="tab" aria-selected={selected}
                  onClick={() => updateFilters({ sortField: option.sortField, sortOrder: option.sortOrder })}
                  className={`rounded-md px-2 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:px-3 ${selected ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-50"}`}>
                  {t(`courseSearch.${option.key}`)}
                </button>;
              })}
            </div>
          </div>
          {error && <div role="alert" className="rounded-lg border p-4">
            {t("courseSearch.error")}
            <button type="button" className="mx-2 underline" onClick={() => setRetry((value) => value + 1)}>{t("courseSearch.retry")}</button>
          </div>}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {(!loading && !error ? courses : []).map((course) => (
              <div
                key={course._id}
                className="transform transition-transform duration-200 hover:-translate-y-1"
              >
                <CourseItem course={course} />
              </div>
            ))}
          </div>

          {!loading && !error && courses.length === 0 && (
            <div className="text-center py-24 bg-gray-50/50 rounded-3xl border border-gray-100 mt-4">
              <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <FaSearch className="text-gray-300 text-xl" />
              </div>
              <Typography variant="h6" color="blue-gray" className="mb-1">
                {t("no_results", "No courses match your search")}
              </Typography>
              <Button
                variant="filled"
                color="indigo"
                size="sm"
                onClick={resetFilters}
              >
                {t("clear_all", "Reset Filters")}
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-20">
              <Spinner color="indigo" className="h-8 w-8" />
            </div>
          )}

          <nav className="flex flex-wrap justify-center items-center gap-4 py-8" aria-label={t("courseSearch.pagination")}>
            <Button variant="outlined" color="indigo" disabled={loading || page <= 1} onClick={() => update("page", page - 1)}>{t("courseSearch.previous")}</Button>
            <span aria-live="polite">{page} / {Math.max(1, totalPages)}</span>
            <Button variant="outlined" color="indigo" disabled={loading || !!error || page >= totalPages || page >= 1000} onClick={() => update("page", page + 1)}>{t("courseSearch.next")}</Button>
          </nav>
        </main>

        <aside className={`hidden lg:min-w-0 ${isEnglish ? "lg:col-start-1 lg:row-start-1 lg:block" : "lg:block"}`}>
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h2 className="text-lg font-bold text-slate-900">{t("filters", "Filters")}</h2>
              {activeFilters.length > 0 && <button type="button" onClick={reset} className="text-sm font-semibold text-indigo-700 underline focus:ring-2 focus:ring-indigo-500">{t("courseSearch.reset")}</button>}
            </div>
            {activeFilters.length > 0 && <div className="border-b border-slate-100 py-4" aria-label={t("courseSearch.active")}>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map(([key, value]) => <button key={key} type="button" onClick={() => update(key, "")}
                  aria-label={`${t("courseSearch.remove")}: ${t(`courseSearch.${key}`, key)} ${filterValue(key, value)}`}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-100 focus:ring-2 focus:ring-indigo-500">
                  <span className="truncate">{key === "keyword" || key === "q" ? filterValue(key, value) : `${t(`courseSearch.${key}`, key)}: ${filterValue(key, value)}`}</span><FaTimes aria-hidden="true" />
                </button>)}
              </div>
            </div>}
            <div className="pt-5">{filterFields}</div>
          </div>
        </aside>
      </div>

      {openMobileFilters && <div id="mobile-course-filters" className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={t("filters", "Filters")}>
        <button type="button" className="absolute inset-0 bg-slate-900/40" onClick={() => setOpenMobileFilters(false)} aria-label={t("close", "Close filters")} />
        <div className="absolute inset-y-0 end-0 flex w-full max-w-sm flex-col overflow-y-auto bg-white p-5 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4"><h2 className="text-lg font-bold">{t("filters", "Filters")}</h2><button type="button" onClick={() => setOpenMobileFilters(false)} className="rounded-full p-2 text-slate-600 hover:bg-slate-100 focus:ring-2 focus:ring-indigo-500" aria-label={t("close", "Close filters")}><FaTimes /></button></div>
          {activeFilters.length > 0 && <div className="border-b border-slate-100 py-4">
            <div className="mb-3 flex flex-wrap gap-2" aria-label={t("courseSearch.active")}>
              {activeFilters.map(([key, value]) => <button key={key} type="button" onClick={() => update(key, "")}
                aria-label={`${t("courseSearch.remove")}: ${t(`courseSearch.${key}`, key)} ${filterValue(key, value)}`}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-100 focus:ring-2 focus:ring-indigo-500">
                <span className="truncate">{key === "keyword" || key === "q" ? filterValue(key, value) : `${t(`courseSearch.${key}`, key)}: ${filterValue(key, value)}`}</span><FaTimes aria-hidden="true" />
              </button>)}
            </div>
            <button type="button" onClick={reset} className="text-sm font-semibold text-indigo-700 underline focus:ring-2 focus:ring-indigo-500">{t("courseSearch.reset")}</button>
          </div>}
          <div className="pt-5">{filterFields}</div>
        </div>
      </div>}
    </section>
  );
};

export default Courses;
